import { Components } from './styled';
import ProjectTable from '../ProjectTable';
import { tableColumns } from '../ProjectTable/constants';
import React from 'react';
import { parseDateToStringWithFormat } from '../../utils/dateFormat';
import { format } from '../../utils/dateFormat/types';
import {
  FavoriteState,
  Deal,
  LikeState,
  PlaybackState,
  ProfileStats,
  SubscriptionInterval,
} from '../../api/types/others';
import {
  CustomSpecialTagsData,
  CustomSubscriptionData,
  CustomUserLifetimeData,
  PreprocessedProfileField,
  UserProfileFieldTypes,
} from './types';

const UserProfileField = ({ displayName, fieldType, data, extraData }: PreprocessedProfileField) => {
  const smallTablePageSize = 10;

  const unknownTypeField = (description: string, value: any) => {
    return (
      <Components.FieldWrapper>
        <Components.FieldLabel>{description}</Components.FieldLabel>
        <pre>
          <Components.FieldBasic>{JSON.stringify(value, null, 8)}</Components.FieldBasic>
        </pre>
      </Components.FieldWrapper>
    );
  };

  const renderedField = () => {
    switch (fieldType) {
      case UserProfileFieldTypes.text:
        return (
          <Components.FieldWrapper>
            <Components.FieldLabel>{displayName}</Components.FieldLabel>
            <Components.FieldBasic>{data as string}</Components.FieldBasic>
          </Components.FieldWrapper>
        );

      case UserProfileFieldTypes.list:
        return (
          <Components.FieldWrapper>
            {displayName && <Components.FieldLabel>{displayName}</Components.FieldLabel>}
            <ProjectTable
              data={[...((data || []) as string[])]?.sort()}
              columns={tableColumns.basicSingleText(extraData || '')}
              customPageSize={smallTablePageSize}
            />
          </Components.FieldWrapper>
        );

      case UserProfileFieldTypes.table:
        switch (extraData) {
          case 'playbackHistory':
            const playbackHistoryData = data as PlaybackState[];
            return (
              <Components.FieldWrapper>
                <Components.FieldLabel>{displayName}</Components.FieldLabel>
                <ProjectTable
                  data={playbackHistoryData || []}
                  columns={tableColumns.profileSessionPlayback}
                  customPageSize={smallTablePageSize}
                  sortingModel={{ field: 'updateDate', sort: 'desc' }}
                />
              </Components.FieldWrapper>
            );

          case 'contentRating':
            const contentRatingData = data as LikeState[];
            return (
              <Components.FieldWrapper>
                <Components.FieldLabel>{displayName}</Components.FieldLabel>
                <ProjectTable
                  data={contentRatingData || []}
                  columns={tableColumns.profileContentRating}
                  customPageSize={smallTablePageSize}
                  sortingModel={{ field: 'updateDate', sort: 'desc' }}
                />
              </Components.FieldWrapper>
            );

          case 'favoriteContent':
            const favoriteContentData = data as FavoriteState[];
            return (
              <Components.FieldWrapper>
                <Components.FieldLabel>{displayName}</Components.FieldLabel>
                <ProjectTable
                  data={favoriteContentData || []}
                  columns={tableColumns.profileLinkedContentBasic}
                  customPageSize={smallTablePageSize}
                  sortingModel={{ field: 'updateDate', sort: 'desc' }}
                />
              </Components.FieldWrapper>
            );

          case 'gifts':
            return <></>;

          default:
            return unknownTypeField(displayName, data);
        }

      case UserProfileFieldTypes.custom:
        switch (extraData) {
          case 'stats':
            const statsData = data as ProfileStats;
            return (
              <Components.LengthInputsWrapper>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Finished sessions</Components.FieldLabel>
                  <Components.FieldBasic>{`${statsData.finishedContent}`}</Components.FieldBasic>
                </Components.FieldWrapper>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Finished daily alux lessons</Components.FieldLabel>
                  <Components.FieldBasic>{`${statsData.finishedDailyLessons}`}</Components.FieldBasic>
                </Components.FieldWrapper>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Longest streak</Components.FieldLabel>
                  <Components.FieldBasic>{`${statsData.longestStreak}`}</Components.FieldBasic>
                </Components.FieldWrapper>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Last streak</Components.FieldLabel>
                  <Components.FieldBasic>{`${statsData.dailyStreak}`}</Components.FieldBasic>
                </Components.FieldWrapper>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Last streak time</Components.FieldLabel>
                  <Components.FieldBasic>{`${
                    statsData.dailyStreakAt
                      ? parseDateToStringWithFormat(statsData.dailyStreakAt, format.weekDayMonthDayYearTime)
                      : ''
                  }`}</Components.FieldBasic>
                </Components.FieldWrapper>
              </Components.LengthInputsWrapper>
            );
          case 'firstTrial':
            const trialData = data as SubscriptionInterval;
            return (
              <Components.LengthInputsWrapper>
                {!!trialData?.purchaseDate && (
                  <Components.FieldWrapper>
                    <Components.FieldLabel>First trial started at</Components.FieldLabel>
                    <Components.FieldBasic>
                      {parseDateToStringWithFormat(trialData?.purchaseDate, format.weekDayMonthDayYearTime)}
                    </Components.FieldBasic>
                  </Components.FieldWrapper>
                )}

                {!!trialData?.expirationDate && (
                  <>
                    <Components.FieldWrapper>
                      <Components.FieldLabel>First trial expires at</Components.FieldLabel>
                      <Components.FieldBasic>
                        {parseDateToStringWithFormat(trialData?.expirationDate, format.weekDayMonthDayYearTime)}
                      </Components.FieldBasic>
                    </Components.FieldWrapper>

                    <Components.FieldWrapper>
                      <Components.FieldLabel>Current trial status</Components.FieldLabel>
                      <Components.CheckboxFieldWrapper>
                        <Components.Checkbox
                          checked={trialData?.expirationDate > new Date().getTime() || false}
                          disabled
                        />
                        <Components.CheckboxLabel>Still active</Components.CheckboxLabel>
                      </Components.CheckboxFieldWrapper>
                    </Components.FieldWrapper>
                  </>
                )}
              </Components.LengthInputsWrapper>
            );

          case 'subscription':
            const subscriptionData = data as CustomSubscriptionData;
            return (
              <>
                <Components.LengthInputsWrapper>
                  <Components.FieldWrapper>
                    <Components.FieldLabel>Last subscription type</Components.FieldLabel>
                    <Components.FieldBasic>{subscriptionData?.subscriptionId || ''}</Components.FieldBasic>
                  </Components.FieldWrapper>

                  {subscriptionData?.subscriptionTransaction && (
                    <Components.FieldWrapper>
                      <Components.FieldLabel>Subscription transaction ID</Components.FieldLabel>
                      <Components.FieldBasic>{subscriptionData?.subscriptionTransaction || ''}</Components.FieldBasic>
                    </Components.FieldWrapper>
                  )}

                  {subscriptionData?.subscriptionExpirationDate && (
                    <>
                      <Components.FieldWrapper>
                        <Components.FieldLabel>Last subscription expired at</Components.FieldLabel>
                        <Components.FieldBasic>
                          {parseDateToStringWithFormat(
                            subscriptionData?.subscriptionExpirationDate,
                            format.weekDayMonthDayYearTime,
                          )}
                        </Components.FieldBasic>
                      </Components.FieldWrapper>

                      <Components.FieldWrapper>
                        <Components.FieldLabel>Current subscription status</Components.FieldLabel>
                        <Components.CheckboxFieldWrapper>
                          <Components.Checkbox
                            checked={subscriptionData?.subscriptionExpirationDate > new Date().getTime() || false}
                            disabled
                          />
                          <Components.CheckboxLabel>Still active</Components.CheckboxLabel>
                        </Components.CheckboxFieldWrapper>
                      </Components.FieldWrapper>
                    </>
                  )}
                </Components.LengthInputsWrapper>
              </>
            );

          case 'userLifetime':
            const userLifetimeData = data as CustomUserLifetimeData;
            return (
              <Components.LengthInputsWrapper>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Profile created at</Components.FieldLabel>
                  <Components.FieldBasic>
                    {parseDateToStringWithFormat(userLifetimeData?.createdAt, format.weekDayMonthDayYearTime)}
                  </Components.FieldBasic>
                </Components.FieldWrapper>
                {!!userLifetimeData?.updatedAt && (
                  <Components.FieldWrapper>
                    <Components.FieldLabel>Profile last updated at</Components.FieldLabel>
                    <Components.FieldBasic>
                      {parseDateToStringWithFormat(userLifetimeData?.updatedAt, format.weekDayMonthDayYearTime)}
                    </Components.FieldBasic>
                  </Components.FieldWrapper>
                )}
                {!!userLifetimeData?.lastSeenAt && (
                  <Components.FieldWrapper>
                    <Components.FieldLabel>User last seen at</Components.FieldLabel>
                    <Components.FieldBasic>
                      {parseDateToStringWithFormat(userLifetimeData?.lastSeenAt, format.weekDayMonthDayYearTime)}
                    </Components.FieldBasic>
                  </Components.FieldWrapper>
                )}
              </Components.LengthInputsWrapper>
            );

          case 'specialTags':
            const specialTagsData = data as CustomSpecialTagsData;
            return (
              <Components.FieldWrapper>
                <Components.FieldLabel>Special tags</Components.FieldLabel>
                <Components.CheckboxFieldWrapper>
                  <Components.Checkbox checked={specialTagsData?.isAdmin} disabled />
                  <Components.CheckboxLabel>Admin</Components.CheckboxLabel>
                </Components.CheckboxFieldWrapper>
                <Components.CheckboxFieldWrapper>
                  <Components.Checkbox checked={specialTagsData?.isTester} disabled />
                  <Components.CheckboxLabel>Tester</Components.CheckboxLabel>
                </Components.CheckboxFieldWrapper>
              </Components.FieldWrapper>
            );

          default:
            return unknownTypeField(displayName, data);
        }

      default:
        return unknownTypeField(displayName, data);
    }
  };

  return <>{renderedField()}</>;
};

export default UserProfileField;
